import * as React from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  CloudUpload,
  Files,
  MonitorSmartphone,
  PenTool,
  Search,
  ShieldCheck,
  Sparkles,
  Timer,
  UserRound,
  Wrench,
} from "lucide-react";
import { ParallelogramButton } from "@/components/ParallelogramButton";
import { StarField } from "@/components/StarField";
import { useInView } from "@/hooks/useInView";

const SKETCHES = {
  wireframe: "/sketch-wireframe.png",
  phone: "/sketch-phone.png",
  design: "/sketch-design.png",
  devices: "/sketch-geraete.png",
  brand: "/sketch-marke.png",
};

const PACKAGE_ITEMS = [
  {
    icon: PenTool,
    title: "Individuelles Design",
    text: "Kein Baukasten – gestaltet passend zu deinem Unternehmen.",
  },
  {
    icon: Files,
    title: "Bis zu 5 Unterseiten",
    text: "Startseite, Leistungen, Über uns, Kontakt und eine weitere Seite.",
  },
  {
    icon: MonitorSmartphone,
    title: "Optimiert für alle Geräte",
    text: "Perfekte Darstellung auf Smartphone, Tablet und Desktop.",
  },
  {
    icon: Wrench,
    title: "Wartung & Updates",
    text: "Technik, Sicherheit und regelmäßige Updates inklusive.",
  },
  {
    icon: Search,
    title: "SEO-Grundstruktur",
    text: "Saubere Basis für bessere Auffindbarkeit bei Google und Co.",
  },
  {
    icon: CloudUpload,
    title: "Hosting & SSL inklusive",
    text: "Sichere, schnelle und zuverlässige Bereitstellung.",
  },
];

const HIGHLIGHTS = [
  { icon: Sparkles, label: "Individuell gestaltet" },
  { icon: Timer, label: "Erster Entwurf in 14 Tagen" },
  { icon: ShieldCheck, label: "Hosting inklusive" },
  { icon: UserRound, label: "Persönlich betreut" },
];

const Reveal = ({ children, delay = 0, className = "" }) => {
  const [ref, inView] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-[opacity,transform] duration-700 ease-out ${
        inView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

/** Skizzen-Collage: Wireframes + handschriftliche Notizen, die aufeinander zeigen. */
/** Handgezeichneter Pfeil (Basisrichtung: nach rechts unten). */
const SketchArrow = ({ className = "", flipX = false, flipY = false }) => (
  <svg
    aria-hidden
    viewBox="0 0 100 100"
    fill="none"
    className={`pointer-events-none absolute text-[hsl(205_100%_68%)] ${className}`}
    style={{
      transform: `scale(${flipX ? -1 : 1}, ${flipY ? -1 : 1})`,
    }}
  >
    <path
      d="M6 12 C 38 16, 58 34, 80 70"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
    />
    <path
      d="M80 70 L 60 63 M80 70 L 76 48"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
    />
  </svg>
);

/** Notiz in Handschrift – alle Notizen nutzen dieselbe Größe. */
const HandNote = ({ children, className = "", align = "left" }) => (
  <span
    // Schriftgroesse skaliert mit der Collage-Breite (Container Query Units)
    style={{ fontSize: "clamp(11px, 4.4cqw, 32px)" }}
    className={`pointer-events-none absolute font-hand leading-[1.15] text-[hsl(203_100%_76%)] drop-shadow-[0_0_14px_hsl(205_100%_60%/0.45)] ${
      align === "right" ? "text-right" : "text-left"
    } ${className}`}
  >
    {children}
  </span>
);

/**
 * Collage nach Inspo: Laptop und Handy nebeneinander,
 * ringsum handschriftliche Notizen, deren Pfeile auf die Geräte zeigen.
 */
const SketchCollage = () => (
  <div
    className="relative mx-auto aspect-[16/12] w-full max-w-[780px]"
    style={{ containerType: "inline-size" }}
  >
    <div
      aria-hidden
      className="absolute left-[4%] top-[26%] -z-10 h-[50%] w-[62%] rounded-full bg-[radial-gradient(closest-side,hsl(210_100%_60%/0.22),transparent)] blur-2xl"
    />
    <div
      aria-hidden
      className="absolute left-[68%] top-[26%] -z-10 h-[44%] w-[24%] rounded-full bg-[radial-gradient(closest-side,hsl(210_100%_60%/0.20),transparent)] blur-2xl"
    />

    {/* Laptop / Browser-Wireframe */}
    <img
      src={SKETCHES.wireframe}
      alt="Website-Wireframe Skizze"
      className="absolute left-[4%] top-[26%] w-[60%] drop-shadow-[0_18px_50px_hsl(215_100%_50%/0.30)] motion-safe:animate-float"
      style={{ animationDuration: "9s", animationDelay: "-1.5s" }}
    />

    {/* Handy-Wireframe */}
    <img
      src={SKETCHES.phone}
      alt="Smartphone-Wireframe Skizze"
      className="absolute left-[70%] top-[24%] w-[14%] drop-shadow-[0_14px_40px_hsl(215_100%_50%/0.32)] motion-safe:animate-float"
      style={{ animationDuration: "7.5s", animationDelay: "-3s" }}
    />

    {/* Oben links – zeigt auf den Laptop */}
    <HandNote className="left-[0%] top-[1%] w-[30%]">
      Individuelles
      <br />
      Design
    </HandNote>
    <SketchArrow className="left-[20%] top-[13%] h-[13%] w-[10%]" />

    {/* Oben rechts – zeigt auf den Laptop */}
    <HandNote className="right-[0%] top-[1%] w-[38%]" align="right">
      SEO-Optimierung für
      <br />
      bessere Google Rankings
    </HandNote>
    <SketchArrow className="right-[40%] top-[16%] h-[13%] w-[10%]" flipX />

    {/* Unten links – zeigt auf den Laptop */}
    <HandNote className="bottom-[1%] left-[0%] w-[27%]">
      5 Unterseiten
      <br />
      inklusive
    </HandNote>
    <SketchArrow className="bottom-[17%] left-[13%] h-[13%] w-[10%]" flipY />

    {/* Unten mittig – zeigt auf den Laptop */}
    <HandNote className="bottom-[1%] left-[33%] w-[33%]">
      Conversion-optimiert
      <br />
      für mehr Anfragen
    </HandNote>
    <SketchArrow className="bottom-[17%] left-[38%] h-[13%] w-[10%]" flipY />

    {/* Unten rechts – zeigt auf das Handy */}
    <HandNote className="bottom-[1%] right-[0%] w-[28%]" align="right">
      Für alle Geräte
      <br />
      angepasst
    </HandNote>
    <SketchArrow className="bottom-[17%] right-[14%] h-[13%] w-[10%]" flipX flipY />
  </div>
);

const PackageCard = ({ item, index }) => {
  const { icon: Icon, title, text } = item;
  return (
    <Reveal delay={index * 70} className="h-full">
      <article className="group relative flex h-full gap-4 overflow-hidden border border-border/70 bg-[hsl(230_30%_7%/0.7)] p-5 backdrop-blur-sm transition-[transform,border-color,background-color] duration-500 hover:-translate-y-1 hover:border-primary/50 hover:bg-[hsl(230_35%_9%/0.85)]">
        <span
          aria-hidden
          className="absolute inset-x-0 top-0 h-px bg-gradient-hairline opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        />
        <span className="relative flex h-10 w-10 shrink-0 items-center justify-center border border-border/80 bg-[hsl(230_30%_10%)] transition-colors duration-500 group-hover:border-primary/60">
          <Icon className="h-4 w-4 text-primary-glow" strokeWidth={1.4} />
        </span>
        <div className="relative flex flex-col">
          <h3 className="font-display text-base font-light tracking-wide text-foreground">
            {title}
          </h3>
          <p className="mt-1.5 text-[13px] font-light leading-relaxed text-muted-foreground">
            {text}
          </p>
        </div>
      </article>
    </Reveal>
  );
};

export const WebsiteSubscription = () => (
  <section
    id="preise"
    className="grain relative isolate overflow-hidden bg-[hsl(0_0%_0%)] py-24 sm:py-28 lg:py-32"
  >
    <StarField count={45} className="-z-20 opacity-50" />
    <div
      aria-hidden
      className="absolute left-1/2 top-[-15%] -z-20 h-[560px] w-[880px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,hsl(230_90%_50%/0.16),transparent)] blur-3xl"
    />

    <div className="mx-auto max-w-[1500px] px-5 sm:px-8 lg:px-12">
      <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-12 lg:gap-10">
        {/* Text + Preis */}
        <div className="lg:col-span-5">
          <Reveal>
            <p className="font-display text-[11px] font-light uppercase tracking-[0.34em] text-primary-glow">
              Das Website-Abo
            </p>
          </Reveal>

          <Reveal delay={90}>
            <h2 className="mt-6 font-display text-3xl font-light leading-[1.1] tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Alles, was deine Website braucht.
            </h2>
          </Reveal>

          <Reveal delay={150}>
            <div className="mt-7 h-px w-36 bg-gradient-hairline" aria-hidden />
          </Reveal>

          <Reveal delay={210}>
            <p className="mt-7 max-w-xl text-sm font-light leading-relaxed text-muted-foreground sm:text-base">
              Individuelles Webdesign, zuverlässiges Hosting und persönliche
              Betreuung – alles in einem fairen Abo. Ohne Einrichtungsgebühr,
              ohne versteckte Kosten.
            </p>
          </Reveal>

          <Reveal delay={280}>
            <div className="mt-10 flex flex-wrap items-end gap-x-4 gap-y-2">
              <span className="font-display text-6xl font-extralight leading-none tracking-tight text-foreground lg:text-7xl">
                59&nbsp;€
              </span>
              <span className="pb-2 font-display text-lg font-light tracking-wide text-primary-glow">
                / Monat
              </span>
            </div>
            <p className="mt-3 text-xs font-light text-muted-foreground/80">
              netto, zzgl. gesetzlicher Umsatzsteuer
            </p>
          </Reveal>

          <Reveal delay={340}>
            <ul className="mt-8 flex flex-col gap-3">
              {["Keine Einrichtungsgebühr", "Persönlicher Ansprechpartner"].map(
                (item) => (
                  <li key={item} className="flex items-center gap-3">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full border border-primary/50 bg-primary/10">
                      <Check className="h-3 w-3 text-primary-glow" strokeWidth={2} />
                    </span>
                    <span className="text-sm font-light text-muted-foreground">
                      {item}
                    </span>
                  </li>
                )
              )}
            </ul>
          </Reveal>

          <Reveal delay={400}>
            <div className="mt-10 flex flex-col items-start gap-5 sm:flex-row sm:items-center">
              <ParallelogramButton
                variant="primary"
                size="lg"
                className="font-display font-normal"
              >
                Kostenlosen Entwurf anfragen
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </ParallelogramButton>

              <a
                href="#referenzen"
                className="group inline-flex items-center gap-2 font-display text-sm font-light tracking-wide text-foreground/80 transition-colors duration-300 hover:text-foreground"
              >
                Erst Referenzen ansehen
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </Reveal>
        </div>

        {/* Skizzen-Collage */}
        <div className="lg:col-span-7">
          <Reveal delay={180}>
            <SketchCollage />
          </Reveal>
        </div>
      </div>

      {/* Paketinhalte – kompakt, direkt darunter */}
      <div className="mt-16 lg:mt-20">
        <Reveal>
          <div className="flex items-center gap-6">
            <p className="whitespace-nowrap font-display text-[11px] font-light uppercase tracking-[0.34em] text-muted-foreground">
              Im 59 € Paket enthalten
            </p>
            <span className="h-px w-full bg-gradient-hairline" aria-hidden />
          </div>
        </Reveal>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {PACKAGE_ITEMS.map((item, index) => (
            <PackageCard key={item.title} item={item} index={index} />
          ))}
        </div>
      </div>

      {/* Highlights */}
      <Reveal delay={120}>
        <ul className="mt-12 grid grid-cols-1 gap-5 border-t border-border/70 pt-8 sm:grid-cols-2 lg:grid-cols-4">
          {HIGHLIGHTS.map(({ icon: Icon, label }) => (
            <li key={label} className="flex items-center gap-3">
              <Icon className="h-4 w-4 shrink-0 text-primary-glow" strokeWidth={1.5} />
              <span className="text-sm font-light tracking-wide text-muted-foreground">
                {label}
              </span>
            </li>
          ))}
        </ul>
      </Reveal>
    </div>
  </section>
);
